import ufeApi from 'services/api/ufeApi';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import cookieUtils from 'utils/Cookies';
import UUIDv4 from 'utils/UUID';
import Hashing from 'utils/Hashing';
import RCPSCookies from 'utils/RCPSCookies';
import authUtils from 'utils/Authentication';
import { URLS, AUTH_HEADERS } from 'constants/authentication';
import { setAuthOperationInProgress, isAuthOperationInProgress } from 'services/auth/newFlow/authFlow';

const { sha256 } = Hashing;

/**
 *
 * @param {*} body
 * @param {*} options
 * @returns
 */
export const generateTokens = async (body, options = {}) => {
    // Set flag only if not already set (to avoid interfering with parent login call)
    // cookieBasedAuth not to trigger authFlow.execute during generateTokens call
    const shouldManageFlag = !isAuthOperationInProgress();

    const url = '/gway/v1/dotcom/auth/v2/generateToken';

    const headers = {
        'Content-type': 'application/json',
        'x-requested-source': 'web',
        'X-CAUSED-BY-URL': options.headerValue
    };

    if (RCPSCookies.isRCPSAuthEnabled()) {
        headers.deviceId = await authUtils.getFingerPrint();
    }

    const generateTokenPromise = (async () => {
        try {
            const response = await ufeApi.makeRequest(
                url,
                {
                    method: 'POST',
                    body: JSON.stringify(body),
                    headers
                },
                {
                    // Auth endpoint that creates tokens, so skip token check to avoid
                    // circular await deadlock with setAuthOperationInProgress
                    skipTokenCheck: true
                }
            );

            if (response?.errors) {
                throw response.errors[0];
            }

            // Store relevant data from response
            storeGenerateTokenValues(response);

            // Clear flag only if this function set it - BEFORE returning so subsequent calls don't wait
            if (shouldManageFlag) {
                setAuthOperationInProgress(false);
            }

            // Return resp so the login execution can continue per usual
            return response;
        } catch (error) {
            // Clear flag on error too
            if (shouldManageFlag) {
                setAuthOperationInProgress(false);
            }

            if (error?.errors) {
                throw error.errors[0];
            }

            // Preserve original error message
            throw error;
        }
    })();

    // Set flag only if this function should manage it, passing the promise so other calls can wait
    if (shouldManageFlag) {
        setAuthOperationInProgress(true, generateTokenPromise);
    }

    return generateTokenPromise;
};

/**
 *
 * @param {*} generateTokenResp
 */
export const storeGenerateTokenValues = generateTokenResp => {
    const LITHIUM_SSO_TOKEN_COOKIE_NAME = 'lithiumSSO:sephora.qa';
    const lithiumSSOToken = generateTokenResp?.lithiumSsoToken;

    // Success, let's store the lithium sso token in LocalStorage for later.
    Storage.local.setItem(LOCAL_STORAGE.LITHIUM_API_TOKEN, lithiumSSOToken);

    // Store access token since new sdn lith token depends on it.
    Storage.local.setItem(LOCAL_STORAGE.AUTH_ACCESS_TOKEN, generateTokenResp?.tokens?.accessToken);
    Storage.local.setItem(LOCAL_STORAGE.AUTH_REFRESH_TOKEN, generateTokenResp?.tokens?.refreshToken);

    // Store profileId and biAccountId for sdn full call
    Storage.local.setItem(LOCAL_STORAGE.PROFILE_ID, generateTokenResp?.profileId);
    Storage.local.setItem(LOCAL_STORAGE.BI_ACCOUNT_ID, generateTokenResp?.beautyInsiderAccount?.biAccountId || generateTokenResp?.biAccountId);

    // Store profileSecurityStatus in localStorage (required by authFlow scenario detection)
    if (generateTokenResp?.tokens?.profileSecurityStatus != null) {
        Storage.local.setItem(LOCAL_STORAGE.PROFILE_SECURITY_STATUS, generateTokenResp.tokens.profileSecurityStatus);
    }

    // Update cookie to hold lithium sso token
    cookieUtils.write(LITHIUM_SSO_TOKEN_COOKIE_NAME, lithiumSSOToken, null, true, false);

    // Set profileStatus cookie for cookie-based auth flow
    // This must be set synchronously here (not in updateProfileStatus which uses async import)
    // to ensure it exists before getUserFull API call
    if (
        RCPSCookies.isRCPSCookieBasedAuthEnabled() &&
        generateTokenResp?.tokens?.profileSecurityStatus != null &&
        generateTokenResp?.tokens?.atExp != null
    ) {
        try {
            const profileStatusTTL = generateTokenResp.tokens.atExp; // Unix timestamp in seconds
            const profileStatusCookieValue = `${generateTokenResp.tokens.profileSecurityStatus}|${profileStatusTTL}`;

            // Set the profileStatus cookie with the appropriate TTL
            // profileStatusTTL: expiration timestamp in seconds (Unix time)
            const nowMs = Date.now();
            const expirationMs = profileStatusTTL * 1000;

            const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;

            // Remaining time until expiration (in ms)
            const remainingMs = expirationMs - nowMs;

            // Convert remaining time to days for cookie maxAge
            const maxAgeInDays = remainingMs / MILLISECONDS_PER_DAY;

            // Set 'profileStatus' (legacy) for backward compatibility
            cookieUtils.write('profileStatus', profileStatusCookieValue, maxAgeInDays, true, false);
        } catch (error) {
            // Cookie setting failed - continue without blocking login
        }
    }
};

export const loginSSO = async ssoToken => {
    const shouldManageFlag = !isAuthOperationInProgress();

    const url = URLS.LOGIN_SSO_URL;

    const headers = {
        'Content-type': 'application/json',
        'x-requested-source': 'web'
    };

    if (RCPSCookies.isRCPSAuthEnabled()) {
        headers.deviceId = await authUtils.getFingerPrint();
    }

    const generateTokenSSOPromise = (async () => {
        try {
            const response = await ufeApi.makeRequest(
                url,
                {
                    method: 'POST',
                    body: JSON.stringify({ ssoToken }),
                    headers
                },
                {
                    skipTokenCheck: true
                }
            );

            if (response?.errors) {
                throw response.errors[0];
            }

            storeGenerateTokenValues(response);

            if (shouldManageFlag) {
                setAuthOperationInProgress(false);
            }

            return response;
        } catch (error) {
            if (shouldManageFlag) {
                setAuthOperationInProgress(false);
            }

            if (error?.errors) {
                throw error.errors[0];
            }

            throw error;
        }
    })();

    if (shouldManageFlag) {
        setAuthOperationInProgress(true, generateTokenSSOPromise);
    }

    return generateTokenSSOPromise;
};

/**
 *
 * @param {*} email
 * @param {*} password
 * @returns
 *
 * Allows us to invoke generateTokens from files other than
 * just login.js
 */
export const manuallyGenerateTokens = async (email, password) => {
    const randomNum = UUIDv4();
    const loginAccessToken = await simpleLoginCall(email, randomNum, password);

    const body = {
        email,
        password,
        randomNumber: randomNum,
        token: loginAccessToken
    };

    return await generateTokens(body);
};

/**
 *
 * @param {*} email
 * @param {*} randomNum
 * @returns
 *
 * Slimmed down version of our full login call in login.js
 * Runs only essential portion of login to provide /generateToken call
 * with needed access token
 */
const simpleLoginCall = async (email, randomNum, password) => {
    const hashedNum = await sha256(randomNum);
    let url = URLS.ATG_LOGIN_URL;
    const headers = { ...AUTH_HEADERS };

    if (RCPSCookies.isRCPSAuthEnabled()) {
        url = URLS.SDN_LOGIN_URL;
        headers.deviceId = await authUtils.getFingerPrint();
    }

    const body = {
        email,
        password,
        randomNumber: hashedNum
    };

    try {
        const response = await ufeApi.makeRequest(
            url,
            {
                method: 'POST',
                body: JSON.stringify(body),
                headers
            },
            {
                // simpleLoginCall is an auth endpoint that creates tokens, not consumes them.
                // Must skip token check to prevent deadlock with setAuthOperationInProgress.
                skipTokenCheck: true
            }
        );

        if (response?.errors) {
            throw response.errors[0];
        }

        // Returns needed access token required by /generateToken
        return response.token;
    } catch (error) {
        throw new Error(error);
    }
};

export default { generateTokens };
